import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { of, from } from 'rxjs';
import { AuthActions } from './auth.actions';
import { AuthService } from '../../services/auth.service';
import { LanguageService } from '../../services/language.service';
import { EncryptionService } from '../../zk/encryption.service';
import { KeyManagementService } from '../../zk/key-management.service';
import { AuthResult, MeResponse } from '../../models/auth.model';

@Injectable()
export class AuthEffects {
  private actions$ = inject(Actions);
  private authService = inject(AuthService);
  private router = inject(Router);
  private languageService = inject(LanguageService);
  private encryption = inject(EncryptionService);
  private keyMgmt = inject(KeyManagementService);

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      mergeMap(({ username, password }) =>
        this.authService.login({ username, password }).pipe(
          mergeMap(result => from(this.decryptAuthResult(result))),
          map(result => AuthActions.loginSuccess({ result })),
          catchError(err => of(AuthActions.loginFailure({
            error: err.error?.message ?? 'Login failed. Please check your credentials.'
          })))
        )
      )
    )
  );

  loginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginSuccess),
      tap(({ result }) => {
        // Apply preferred language from server
        this.languageService.set(result.preferredLanguage === 'en' ? 'en' : 'ar');
        this.router.navigate(['/']);

        // Best-effort background ZK session setup — unwraps tenant keys (k_phone).
        // Fire-and-forget: does not block navigation, failures are non-fatal.
        // Pending share requests are no longer auto-resolved here — a holder must
        // explicitly review and resolve or deny each one (via a record's own Access modal,
        // or the /settings/zk-access hub), since silent login-triggered auto-grant bypassed
        // RBAC with no human approval.
        this.keyMgmt.initSession()
          .catch(err => console.warn('ZK session init failed:', err));
      })
    ),
    { dispatch: false }
  );

  loadMe$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loadMe),
      mergeMap(() =>
        this.authService.me().pipe(
          mergeMap(me => from(this.decryptMeResponse(me))),
          map(me => AuthActions.loadMeSuccess({ me })),
          catchError(() => of(AuthActions.loadMeFailure({ error: 'Session expired.' })))
        )
      )
    )
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      tap(() => this.encryption.send({ type: 'CLEAR_DEK_CACHE', payload: {} })),
      mergeMap(() =>
        this.authService.logout().pipe(
          map(() => AuthActions.logoutSuccess()),
          catchError(() => of(AuthActions.logoutSuccess()))
        )
      )
    )
  );

  logoutSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logoutSuccess),
      tap(() => {
        this.authService.clearToken();
        this.router.navigate(['/login']);
      })
    ),
    { dispatch: false }
  );

  changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.changePassword),
      mergeMap(({ currentPassword, newPassword }) =>
        this.authService.changePassword({ currentPassword, newPassword }).pipe(
          map(() => AuthActions.changePasswordSuccess()),
          catchError(err => of(AuthActions.changePasswordFailure({
            error: err.error?.message ?? 'Password change failed.'
          })))
        )
      )
    )
  );

  // ── Decryption helpers ────────────────────────────────────────────────────

  private async decryptAuthResult(result: AuthResult): Promise<AuthResult> {
    if (!result.wrappedDek || !result.encryptedFields || Object.keys(result.encryptedFields).length === 0) {
      return result;
    }
    try {
      const plain = await this.encryption.decryptRecord(result.encryptedFields, result.wrappedDek);
      return {
        ...result,
        fullName: plain['fullName'] ?? result.fullName,
        email: plain['email'] ?? result.email,
      };
    } catch {
      return result;
    }
  }

  private async decryptMeResponse(me: MeResponse): Promise<MeResponse> {
    if (!me.wrappedDek || !me.encryptedFields || Object.keys(me.encryptedFields).length === 0) {
      return me;
    }
    try {
      const plain = await this.encryption.decryptRecord(me.encryptedFields, me.wrappedDek);
      return {
        ...me,
        fullName: plain['fullName'] ?? me.fullName,
        email: plain['email'] ?? me.email,
      };
    } catch {
      return me;
    }
  }
}
